import React, { ReactNode } from "react";
import { Types as AlchemistTypes } from "alchemist";
import dynamic from "next/dynamic";
import {
    Paragraph,
    Link as LinkComponent,
    ArticleImage,
    Embed,
    OrderedList,
    UnorderedList,
    Subhead,
    Blockquote,
    Pullquote,
    SectionBreak,
    Table,
    FootnoteItem,
    FootnoteLink,
    ByTheNumbers,
} from "@bloomberg/media-ui-article";
import { TerminalWhite } from "@bloomberg/media-ui-icons";
import { BLOCKS, CommonNode } from "@foundation/rich-text-react-renderer";

import { TABLE_CELL_CLASS_TYPES } from "~constants";
import BasicRichtextParagraph from "~components/articles/basic/BasicRichtextParagraph";
import Chart, { ChartProps } from "~components/Chart";
import ErrorBoundary from "~components/ErrorBoundary";
import InlineNewsletter from "~components/InlineNewsletter";
import InlineRecircComponent from "~components/InlineRecirc";
import StoryInBodyAd from "~components/StoryInBodyAd";
import { ToasterChart, ToasterChartProps } from "~components/ToasterChart";
import ViewportComponent from "~components/ViewportComponent";
import { ENTITY_SUBTYPES, MEDIA_SUBTYPES } from "~types/types";
import { ANIMATED_GIF } from "~utils";
import isEnabled from "~utils/features";
import { getSecurityUrl } from "~utils/securityLinkUtil";

enum HEADER_LEVELS {
    CHAPTER_HEAD = 2,
    SUB_HEAD = 3,
}

const InlineVideoPlaylistPlayer = dynamic(
    () => import("../~components/VideoPlayer/InlineVideoPlaylistPlayer")
);

const InlineVideoPlayer = dynamic(
    () => import("../~components/VideoPlayer/InlineVideoPlayer")
);

const InlineAudioPlayer = dynamic(
    () => import("../~components/AudioPlayer/InlineAudioPlayer")
);

interface RichtextMediaProps {
    node: CommonNode;
    className?: string;
}

interface RichtextEntityProps {
    node: CommonNode;
    children?: ReactNode;
}

export const RichtextMedia = ({ node, className = "" }: RichtextMediaProps) => {
    const subType = (node as unknown as AlchemistTypes.Media).subType;

    if (subType === MEDIA_SUBTYPES.CHART) {
        const alchemistChart = node as unknown as AlchemistTypes.Chart;

        if (alchemistChart.data.attachment) {
            const { creator } = alchemistChart.data.attachment;

            if (creator === "TOASTER") {
                const toasterOptions: ToasterChartProps = {
                    src: alchemistChart.data.chart?.fallback || "",
                    chart: alchemistChart.data.attachment,
                    id: alchemistChart.data.chart?.id || "",
                    alt: alchemistChart.data.chart?.alt || "",
                };
                return (
                    <ViewportComponent
                        onRender={(inViewport) =>
                            inViewport && <ToasterChart {...toasterOptions} />
                        }
                    />
                );
            }

            let aspectRatio;
            if (alchemistChart.data.attachment.aspectRatio) {
                const ratio =
                    alchemistChart.data.attachment.aspectRatio.split(":");
                aspectRatio = Number(ratio[0]) / Number(ratio[1]);
            }
            const chartProps: ChartProps = {
                aspectRatio: aspectRatio || null,
                footnote: alchemistChart.data.attachment.footnote || "",
                title: alchemistChart.data.attachment.title || "",
                subtitle: alchemistChart.data.attachment.subtitle || "",
                source: alchemistChart.data.attachment.source || "",
                src: alchemistChart.data.chart?.fallback || "",
            };

            return <Chart {...chartProps} />;
        }
    }

    if (subType === MEDIA_SUBTYPES.PHOTO) {
        const { data: photoData } = node as unknown as AlchemistTypes.Photo;
        let baseUrl = photoData.photo?.src ?? "";

        if (photoData.attachment) {
            // isAllowed provided by fortress, isLazy set to true for now, aspectRatio-parseRSF, caption has weird strings <figcaption>Amazon&amp;#x2019;s HQ2 development in 2021.</figcaption>
            let derivedAspectRatio;
            if (photoData.attachment.width && photoData.attachment.height) {
                derivedAspectRatio =
                    photoData.attachment.width / photoData.attachment.height;
            }

            if (ANIMATED_GIF.test(baseUrl)) {
                baseUrl = baseUrl
                    .replace(/^http:\/\//, "//")
                    .replace(/^\/\//, "https://")
                    .replace(/.webp/, ".gif");
            }

            const articleImage = (
                <ArticleImage
                    isAllowed
                    isLazy
                    altText={photoData.photo?.alt || ""}
                    src={baseUrl}
                    caption={photoData.photo?.caption || ""}
                    credit={photoData.photo?.credit || ""}
                    width={photoData.attachment.width}
                    height={photoData.attachment.height}
                    aspectRatio={derivedAspectRatio}
                    decoding="async"
                    className={className}
                    isExpandable={!photoData.link?.destination.web}
                />
            );

            if (photoData.link?.destination?.web) {
                return (
                    <LinkComponent
                        href={photoData.link?.destination.web}
                        target={"_blank"}
                        rel="noopener"
                    >
                        {articleImage}
                    </LinkComponent>
                );
            }

            return articleImage;
        }
    }

    if (subType === MEDIA_SUBTYPES.VIDEO) {
        const isVerticalVideoEnabled = isEnabled("enableVerticalVideo");
        // split based on aspectRatio
        // default is playlist player for VOD
        // if vertical video then use the video vod player as there are no suggestions
        const { data: videoData } = node as unknown as AlchemistTypes.Video;

        const id = videoData.attachment?.id;
        const caption = videoData.video?.caption;
        const poster = videoData.attachment?.thumbnail?.url ?? undefined;

        if (!id) return null;

        // todo: update alchemist to support the prop: ENG4VIDEO-3763
        const isVertical =
            (videoData.attachment as unknown as any)?.aspectRatio ===
            "ASPECT_9_16";

        if (isVerticalVideoEnabled && isVertical) {
            return (
                <ViewportComponent
                    onRender={(inViewport: boolean) =>
                        inViewport && (
                            <InlineVideoPlayer
                                options={{
                                    id,
                                    poster,
                                    video: videoData.video,
                                    serviceEnv: "production",
                                }}
                                className={className}
                                caption={caption}
                            />
                        )
                    }
                />
            );
        }
        return (
            <ViewportComponent
                onRender={(inViewport: boolean) =>
                    inViewport && (
                        <>
                            <InlineVideoPlaylistPlayer
                                options={{
                                    video: videoData.video,
                                    id,
                                    poster,
                                    serviceEnv: "production",
                                }}
                                className={className}
                            />
                            {caption && (
                                <figcaption
                                    className="caption-1 gap-1"
                                    dangerouslySetInnerHTML={{
                                        __html: caption,
                                    }}
                                />
                            )}
                        </>
                    )
                }
            />
        );
    }

    if (subType === MEDIA_SUBTYPES.AUDIO && node.data.attachment) {
        const alchemistAudio = node as unknown as AlchemistTypes.Audio;
        const { title, url, image, duration } = alchemistAudio.data.attachment;

        if (title && url) {
            return (
                <InlineAudioPlayer
                    title={title}
                    url={url}
                    durationInMS={Number(duration)}
                    imageUrl={image?.url as string}
                />
            );
        }
    }

    return null;
};

const RichtextEntity = ({ node, children }: RichtextEntityProps) => {
    const {
        subType,
        data: {
            link: {
                destination: { web },
                target,
                webUrl,
            },
        },
    } = node as AlchemistTypes.Person | AlchemistTypes.Story;

    // add terminal command, destination.function.cmd from destination as a link option
    // https://nppub-structured-data.stacker.dev.bloomberg.com/link.html

    // add any person entity specific processing here
    if (subType === ENTITY_SUBTYPES.PERSON) {
        if (webUrl) {
            return (
                <LinkComponent
                    href={webUrl}
                    target={target || "_blank"}
                    rel="noopener"
                >
                    {children}
                </LinkComponent>
            );
        }
        return children;
    }

    // add any story entity specific processing here
    if (subType === ENTITY_SUBTYPES.STORY) {
        if (web) {
            return (
                <LinkComponent
                    href={web}
                    target={target || "_blank"}
                    rel="noopener"
                >
                    {children}
                </LinkComponent>
            );
        }
        const { data } = node as unknown as AlchemistTypes.Story;
        const storyId = data.story.identifiers.suid;
        const url = `https://www.bloomberg.com/news/terminal/${storyId}`;

        return (
            <LinkComponent
                href={url}
                className={"whitespace-normal"}
                target="_blank"
                rel="noopener"
            >
                {children}
                <TerminalWhite className={"inline-flex ml-[4px] mt-[4px]"} />
            </LinkComponent>
        );
    }

    // add any security entity specific processing here
    if (subType === ENTITY_SUBTYPES.SECURITY) {
        const url = getSecurityUrl(node);

        return (
            <LinkComponent href={url} target="_blank" rel="noopener">
                {children}
            </LinkComponent>
        );
    }

    return <>{children}</>;
};

const options = {
    media: (node: CommonNode) => <RichtextMedia node={node} />,

    [BLOCKS.PARAGRAPH]: (
        node: CommonNode,
        children: ReactNode,
        extras: { index: number; prev: string; next: string }
    ) => {
        // add paywall selector if the total number of paragraphs is less than or equal to 2 and we're on the second paragraph
        // otherwise add the selector to every paragraph after the second paragraph
        const usePaywallSelector =
            extras.index > 2 ||
            (node.data.numberOfParagraphs <= 2 && extras.index === 1);

        return (
            <BasicRichtextParagraph
                className={usePaywallSelector ? "paywall" : ""}
                node={node}
            >
                {children}
            </BasicRichtextParagraph>
        );
    },

    blockquote: (_: CommonNode, children: ReactNode) => (
        <Blockquote>{children}</Blockquote>
    ),

    pullquote: (_: CommonNode, children: ReactNode) => (
        <Pullquote>{children}</Pullquote>
    ),

    [BLOCKS.HR]: (_: CommonNode) => <SectionBreak />,

    [BLOCKS.INLINE_RECIRC]: (node: CommonNode) => (
        <InlineRecircComponent inlineRecircKey={node.data.inlineRecircKey} />
    ),

    [BLOCKS.LINK]: (node: CommonNode, children: ReactNode) => {
        const {
            data: {
                destination: { web, bbg },
            },
        } = node as AlchemistTypes.Link;
        const title = node.data.title;

        if (web) {
            return (
                <LinkComponent
                    href={web}
                    title={title}
                    target="_blank"
                    rel="noopener"
                >
                    {children}
                </LinkComponent>
            );
        }

        if (bbg?.startsWith("bbg://news/stories")) {
            const storyId = bbg.split("bbg://news/stories/").pop();
            const url = `https://www.bloomberg.com/news/terminal/${storyId}`;

            return (
                <LinkComponent
                    href={url}
                    title={title}
                    className={"whitespace-normal"}
                    target="_blank"
                    rel="noopener"
                >
                    {children}
                    <TerminalWhite
                        className={"inline-flex ml-[4px] mt-[4px]"}
                    />
                </LinkComponent>
            );
        }

        return children;
    },

    [BLOCKS.EMBED]: (node: CommonNode) => {
        const { href, iframeData } = node as AlchemistTypes.SocialComponent;

        return <Embed html={iframeData?.html} url={href} />;
    },

    [BLOCKS.LIST]: (node: CommonNode, children: React.ReactNode) => {
        const { subType } = node as unknown as AlchemistTypes.List;

        if (subType === "unordered") {
            return <UnorderedList>{children}</UnorderedList>;
        }

        if (subType === "ordered") {
            return <OrderedList>{children}</OrderedList>;
        }

        return null;
    },

    // Future TODO: ListItem should be a generic component in media-ui-monorepo
    [BLOCKS.LIST_ITEM]: (node: CommonNode, children: React.ReactNode) => (
        <UnorderedList.Item>{children}</UnorderedList.Item>
    ),

    [BLOCKS.HEADING]: (node: CommonNode, children: React.ReactNode) => {
        const { data } = node as unknown as AlchemistTypes.Heading;

        // TODO: Add in <ChapterHead> when  WOLF and NCAS are able to differentiate between ChapterHead and Subhead
        if (
            data.level === HEADER_LEVELS.SUB_HEAD ||
            data.level === HEADER_LEVELS.CHAPTER_HEAD
        ) {
            return <Subhead>{children}</Subhead>;
        }

        return null;
    },

    // TODO: Entity also needs to eventually be in rich-text-react-renderer default
    [BLOCKS.ENTITY]: (node: CommonNode, children: ReactNode) => (
        <RichtextEntity node={node}>{children}</RichtextEntity>
    ),

    [BLOCKS.AD]: (node: CommonNode) => (
        <div className="gap-1">
            <ErrorBoundary fallback="failed to render ad">
                <StoryInBodyAd positionIncrement={node.data.num} />
            </ErrorBoundary>
        </div>
    ),

    [BLOCKS.INLINE_NEWSLETTER]: (node: CommonNode) => {
        const { position } = node.data;

        return <InlineNewsletter position={position} />;
    },

    [BLOCKS.TABLE]: (node: CommonNode, children: React.ReactNode) => (
        <Table>{children}</Table>
    ),

    [BLOCKS.TABLE_ROW]: (node: CommonNode, children: React.ReactNode) => (
        <Table.Row>{children}</Table.Row>
    ),

    [BLOCKS.TABLE_CELL]: (node: CommonNode, children: React.ReactNode) => {
        const cellType = TABLE_CELL_CLASS_TYPES[node.data.class] || "text";
        return (
            <Table.Cell type={cellType} span={node.data.colspan}>
                {children}
            </Table.Cell>
        );
    },

    columns: (node: CommonNode) => {
        const columns = node.data.definitions.map(
            (column: { [key: string]: string }) => ({
                title: column.title,
                span: column.colSpan || 1,
                type: column.dataType,
            })
        );

        return <Table.Header headers={columns} />;
    },

    [BLOCKS.FOOTNOTE_LINK]: (node: CommonNode, children: ReactNode) => {
        const id = node.data.identifier;
        const count = id.split("-")[1];

        return (
            <FootnoteLink id={id} count={count}>
                {children}
            </FootnoteLink>
        );
    },

    byTheNumbers: (node: CommonNode, children: ReactNode) => (
        <ByTheNumbers>{children}</ByTheNumbers>
    ),

    callout: (node: CommonNode, children: ReactNode) => (
        <ByTheNumbers.CallOut>{children}</ByTheNumbers.CallOut>
    ),

    [BLOCKS.DIV]: (node: CommonNode, children: ReactNode) => (
        <div className="body-1 gap-1 font-article-body">{children}</div>
    ),
};

export const footnotesOptions = {
    footnotes: (node: CommonNode, children: ReactNode) => <>{children}</>,
    [BLOCKS.FOOTNOTE]: (node: CommonNode, children: ReactNode) => {
        const id = node.data.identifier;

        return <FootnoteItem id={id}>{children}</FootnoteItem>;
    },

    [BLOCKS.PARAGRAPH]: (node: CommonNode, children: ReactNode) => (
        <Paragraph unstyled className={"inline"}>
            {children}
        </Paragraph>
    ),

    [BLOCKS.LINK]: (node: CommonNode, children: ReactNode) => {
        const {
            data: { destination },
        } = node as AlchemistTypes.Link;

        return (
            <LinkComponent
                href={destination.web || destination.bbg}
                rel="noopener"
            >
                {children}
            </LinkComponent>
        );
    },

    [BLOCKS.ENTITY]: (node: CommonNode, children: ReactNode) => (
        <RichtextEntity node={node}>{children}</RichtextEntity>
    ),
};

export const footerOptions = {
    [BLOCKS.PARAGRAPH]: (node: CommonNode, children: ReactNode) => (
        <Paragraph>{children}</Paragraph>
    ),

    [BLOCKS.LINK]: (node: CommonNode, children: ReactNode) => {
        const {
            data: { destination },
        } = node as AlchemistTypes.Link;

        return (
            <LinkComponent href={destination.web} rel="noopener">
                {children}
            </LinkComponent>
        );
    },

    [BLOCKS.ENTITY]: (node: CommonNode, children: ReactNode) => (
        <RichtextEntity node={node}>{children}</RichtextEntity>
    ),
};

export default options;
